// Lightweight properties panel. Renders the editable attributes of the
// currently-selected DRG element (or requirement). Updates mutate the moddle
// tree in place and notify the host via onChange so it can re-render the DRD.
//
// Kept intentionally plain — plain DOM, no Preact/React, no framework.

import './styles.css';
import { FEEL_TYPES } from '../widgets/feelTypes';
import { resolveDecisionInputs } from '../scope/decisionInputs';
import { sanitizeFeelIdent, bindNoSpaces } from '../feel/ident';

const KIND_LABEL = {
  'dmn:Decision': 'Decision',
  'dmn:BusinessKnowledgeModel': 'Business Knowledge Model',
  'dmn:InputData': 'Input Data',
  'dmn:KnowledgeSource': 'Knowledge Source',
  'dmn:DecisionService': 'Decision Service',
  'dmn:InformationRequirement': 'Information Requirement',
  'dmn:KnowledgeRequirement': 'Knowledge Requirement',
  'dmn:AuthorityRequirement': 'Authority Requirement',
};

const KIND_TOKEN = {
  'dmn:Decision': 'decision',
  'dmn:BusinessKnowledgeModel': 'bkm',
  'dmn:InputData': 'input',
  'dmn:KnowledgeSource': 'ks',
  'dmn:DecisionService': 'service',
  'dmn:InformationRequirement': 'requirement',
  'dmn:KnowledgeRequirement': 'requirement',
  'dmn:AuthorityRequirement': 'requirement',
};

const LOGIC_LABEL = {
  'dmn:LiteralExpression': 'Literal Expression',
  'dmn:DecisionTable': 'Decision Table',
  'dmn:Context': 'Context',
  'dmn:List': 'List',
  'dmn:Relation': 'Relation',
  'dmn:Invocation': 'Invocation',
  'dmn:FunctionDefinition': 'Function Definition',
  // EPX extension — see src/moddle/epx-ml.json
  'epx:MachineLearningDecision': 'Machine Learning',
};

export class PropertiesPanel {
  constructor(container, {
    moddle,
    definitions,
    onChange,
    onCreateMissingInputs,
    onSyncBkmBindings,
    onSetInputType,
    onPromoteOrphan,
  } = {}) {
    this._container = container;
    this._moddle = moddle;
    this._definitions = definitions || null;
    this._onChange = onChange || (() => {});
    this._onCreateMissingInputs = onCreateMissingInputs || null;
    this._onSyncBkmBindings = onSyncBkmBindings || null;
    this._onSetInputType = onSetInputType || null;
    this._onPromoteOrphan = onPromoteOrphan || null;
    this._current = null;
    // Per-element variable-name sync state. Decision/BKM/InputData each carry
    // a dmn:InformationItem variable; by default we keep variable.name in sync
    // with the element's name. The user can opt out per element via the link
    // in General → Name to give the variable an independent (FEEL-friendly) name.
    this._varSyncState = new WeakMap();
    this._renderEmpty();
  }

  show(element) {
    this._current = element || null;
    if (!element) return this._renderEmpty();
    this._render(element);
  }

  hide() { this._current = null; this._renderEmpty(); }

  refresh() {
    if (this._current) this._render(this._current);
    else this._renderEmpty();
  }

  setDefinitions(defs) {
    this._definitions = defs || null;
    this.refresh();
  }

  getCurrent() { return this._current; }

  destroy() { this._container.innerHTML = ''; }

  // ── internals ──────────────────────────────────────────────────────

  _renderEmpty() {
    this._container.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'epx-dmn-props__empty';
    const icon = document.createElement('div');
    icon.className = 'epx-dmn-props__empty-icon';
    icon.innerHTML = '<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>';
    const title = document.createElement('div');
    title.className = 'epx-dmn-props__empty-title';
    title.textContent = 'Properties';
    const body = document.createElement('div');
    body.className = 'epx-dmn-props__empty-body';
    body.textContent = 'Select a DRG element to inspect it.';
    wrap.appendChild(icon);
    wrap.appendChild(title);
    wrap.appendChild(body);
    this._container.appendChild(wrap);
  }

  _render(element) {
    this._container.innerHTML = '';
    const root = document.createElement('div');
    root.className = 'epx-dmn-props';

    root.appendChild(this._buildHeader(element));
    root.appendChild(this._buildGeneralGroup(element));

    if (isDecision(element)) {
      root.appendChild(this._buildDecisionLogicGroup(element));
    }
    if (isBkm(element)) {
      root.appendChild(this._buildBkmLogicGroup(element));
    }
    if (isRequirement(element)) {
      root.appendChild(this._buildRequirementGroup(element));
    }
    if ((isDecision(element) || isBkm(element)) && this._definitions) {
      root.appendChild(this._buildInputsGroup(element));
    }

    this._container.appendChild(root);
  }

  _buildInputsGroup(element) {
    const g = group('Inputs');
    const { connected, orphans } = resolveDecisionInputs(this._definitions, element);

    if (!connected.length && !orphans.length) {
      const empty = document.createElement('div');
      empty.className = 'epx-dmn-props__hint';
      empty.textContent = 'No hay inputs detectados.';
      g.appendChild(empty);
      return g;
    }

    const kind = element.$type;
    connected.forEach((entry) => g.appendChild(this._buildInputsRow(entry, false, kind)));
    if (orphans.length) {
      orphans.forEach((entry) => g.appendChild(this._buildInputsRow(entry, true, kind)));
    }
    return g;
  }

  _buildInputsRow(entry, isOrphan, kind) {
    const row = document.createElement('div');
    row.className = 'epx-dmn-props__inputs-row' + (isOrphan ? ' epx-dmn-props__inputs-row--orphan' : '');

    const name = document.createElement('span');
    name.className = 'epx-dmn-props__inputs-name';
    name.textContent = (isOrphan ? '⚠ ' : '') + entry.name;
    row.appendChild(name);

    const sel = typeSelect(entry.typeRef || 'string', (val) => {
      if (this._onSetInputType) this._onSetInputType(entry.name, val || null);
      this._notify();
    });
    sel.classList.add('epx-dmn-props__select--compact');
    row.appendChild(sel);

    if (isOrphan && this._onPromoteOrphan) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'epx-dmn-props__inputs-promote';
      btn.textContent = '＋';
      btn.title = kind === 'dmn:BusinessKnowledgeModel'
        ? 'Agregar parámetro al BKM'
        : 'Crear InputData en el DRD y conectar a esta decisión';
      btn.addEventListener('click', () => this._onPromoteOrphan(entry.name));
      row.appendChild(btn);
    }

    return row;
  }

  _buildHeader(element) {
    const h = document.createElement('div');
    h.className = 'epx-dmn-props__header';

    const top = document.createElement('div');
    top.className = 'epx-dmn-props__header-top';

    const chip = document.createElement('span');
    const token = KIND_TOKEN[element.$type] || 'other';
    chip.className = `epx-dmn-props__kind-chip epx-dmn-props__kind-chip--${token}`;
    chip.textContent = KIND_LABEL[element.$type] || element.$type;
    top.appendChild(chip);

    const title = document.createElement('div');
    title.className = 'epx-dmn-props__title';
    title.textContent = element.name || '(unnamed)';
    if (!element.name) title.classList.add('epx-dmn-props__title--placeholder');
    top.appendChild(title);

    h.appendChild(top);

    if (element.id) {
      const idLine = document.createElement('div');
      idLine.className = 'epx-dmn-props__id';
      const idLabel = document.createElement('span');
      idLabel.className = 'epx-dmn-props__id-label';
      idLabel.textContent = 'ID';
      const idValue = document.createElement('span');
      idValue.className = 'epx-dmn-props__id-value';
      idValue.textContent = element.id;
      idValue.title = element.id;
      idLine.appendChild(idLabel);
      idLine.appendChild(idValue);
      h.appendChild(idLine);
    }

    return h;
  }

  _buildGeneralGroup(element) {
    const g = group('General');
    const hasVar = hasVariable(element);

    let v = null;
    if (hasVar) {
      v = element.variable;
      if (!v) {
        v = this._moddle.create('dmn:InformationItem', {
          id: (element.id || 'Var') + '_var',
          name: sanitizeFeelIdent(element.name) || '',
        });
        element.variable = v;
      }
    }

    const supportsNameField = ('name' in element) || supportsName(element);

    if (supportsNameField) {
      // Sync state: default to "synced" unless the existing model already
      // diverges (legacy file). Once the user toggles, we honour their choice
      // for the rest of the session.
      let synced;
      if (this._varSyncState.has(element)) {
        synced = this._varSyncState.get(element);
      } else {
        synced = !hasVar || ((element.name || '') === (v.name || ''));
        this._varSyncState.set(element, synced);
      }

      const nameInput = text(element.name || '', (val) => {
        element.name = val || null;
        if (hasVar && synced) v.name = sanitizeFeelIdent(val) || null;
        // Re-render so the sync hint reflects the new variable name.
        this._render(element);
        this._notify();
      });
      g.appendChild(field('Name', nameInput));

      if (hasVar) {
        g.appendChild(this._buildVariableSyncRow(element, v, synced));
        if (!synced) {
          const varInput = bindNoSpaces(text(v.name || '', (val) => {
            v.name = sanitizeFeelIdent(val) || null;
            this._render(element);
            this._notify();
          }));
          g.appendChild(field('Variable name', varInput));
        }
        g.appendChild(field('Type', typeSelect(v.typeRef || 'string', (val) => {
          v.typeRef = val || null;
          this._notify();
        })));
      }
    }

    if ('description' in element || supportsDescription(element)) {
      g.appendChild(field('Description', textarea(element.description || '', (val) => {
        element.description = val || null;
        this._notify();
      })));
    }

    return g;
  }

  _buildVariableSyncRow(element, variable, synced) {
    const row = document.createElement('div');
    row.className = 'epx-dmn-props__sync-row';

    const link = document.createElement('button');
    link.type = 'button';
    link.className = 'epx-dmn-props__sync-link' + (synced ? '' : ' epx-dmn-props__sync-link--unlinked');

    const icon = document.createElement('span');
    icon.className = 'epx-dmn-props__sync-icon';
    icon.textContent = synced ? '🔗' : '🔓';
    link.appendChild(icon);

    const label = document.createElement('span');
    label.className = 'epx-dmn-props__sync-label';
    if (synced) {
      label.textContent = 'Variable: ';
      const code = document.createElement('code');
      code.textContent = variable.name || '(empty)';
      label.appendChild(code);
    } else {
      label.textContent = 'Custom variable name';
    }
    link.appendChild(label);

    link.title = synced
      ? 'Click to give the variable a custom name'
      : 'Click to sync the variable name with Name';

    link.addEventListener('click', () => {
      const next = !synced;
      this._varSyncState.set(element, next);
      if (next === true) {
        // Re-syncing: copy the element name into the variable (no spaces).
        variable.name = sanitizeFeelIdent(element.name) || null;
      }
      this._render(element);
      this._notify();
    });

    row.appendChild(link);
    return row;
  }

  _buildDecisionLogicGroup(element) {
    const g = group('Decision Logic');
    const logic = element.decisionLogic;
    const currentType = logic ? logic.$type : '(none)';
    g.appendChild(field('Type', badge(LOGIC_LABEL[currentType] || currentType)));

    if (logic && logic.$type === 'dmn:DecisionTable') {
      const dt = logic;
      const hp = dt.hitPolicy || 'UNIQUE';
      g.appendChild(field('Hit Policy', hitPolicySelect(hp, (val) => {
        dt.hitPolicy = val;
        this._notify();
      })));
      g.appendChild(field('Rules', readonly(String((dt.rule || []).length))));
      g.appendChild(field('Inputs', readonly(String((dt.input || []).length))));
      g.appendChild(field('Outputs', readonly(String((dt.output || []).length))));
    }

    if (logic && logic.$type === 'dmn:LiteralExpression') {
      g.appendChild(field('Text', textarea(logic.text || '', (val) => {
        logic.text = val || null;
        this._notify();
      })));
    }

    if (logic && logic.$type === 'dmn:Invocation' && this._onCreateMissingInputs) {
      const missing = this._missingBkmInputNames(element);
      if (missing.length > 0) {
        const btn = button(`Create missing inputs (${missing.length})`, () => {
          const created = this._onCreateMissingInputs(element.id);
          if (created) this._render(element);
        });
        const hint = document.createElement('div');
        hint.className = 'epx-dmn-props__hint';
        hint.textContent = missing.join(', ');
        const wrap = document.createElement('div');
        wrap.appendChild(btn);
        wrap.appendChild(hint);
        g.appendChild(field('BKM Inputs', wrap));
      }
    }
    return g;
  }

  _missingBkmInputNames(decision) {
    const krs = decision.knowledgeRequirement || [];
    const seen = new Set();
    const names = [];
    krs.forEach((kr) => {
      const href = kr.requiredKnowledge && kr.requiredKnowledge.href;
      if (!href) return;
      const id = href.replace(/^#/, '');
      const defs = decision.$parent;
      if (!defs || !defs.drgElement) return;
      const target = defs.drgElement.find((e) => e.id === id);
      if (!target || target.$type !== 'dmn:BusinessKnowledgeModel') return;
      const fps = (target.encapsulatedLogic && target.encapsulatedLogic.formalParameter) || [];
      fps.forEach((p) => {
        const n = (p.name || '').trim();
        if (!n || seen.has(n)) return;
        seen.add(n);
        names.push(n);
      });
    });
    const connected = new Set();
    (decision.informationRequirement || []).forEach((req) => {
      const ref = req.requiredInput || req.requiredDecision;
      const rid = ref && (ref.href || '').replace(/^#/, '');
      if (!rid) return;
      const defs = decision.$parent;
      const tgt = defs && (defs.drgElement || []).find((e) => e.id === rid);
      if (tgt && tgt.name) connected.add(tgt.name.trim());
    });
    return names.filter((n) => !connected.has(n));
  }

  _buildBkmLogicGroup(element) {
    const g = group('Encapsulated Logic');
    const logic = element.encapsulatedLogic;
    const currentType = logic ? logic.$type : '(none)';
    g.appendChild(field('Type', badge(LOGIC_LABEL[currentType] || currentType)));
    if (logic && logic.$type === 'dmn:FunctionDefinition') {
      const kind = logic.kind || 'FEEL';
      g.appendChild(field('Kind', readonly(kind)));
      g.appendChild(this._buildParametersEditor(logic));
    }
    if (this._onSyncBkmBindings) {
      const connected = this._decisionsInvokingBkm(element);
      if (connected.length > 0) {
        const btn = button(`Actualizar decisiones conectadas (${connected.length})`, () => {
          const updated = this._onSyncBkmBindings(element.id);
          if (updated > 0) this._render(element);
        });
        const hint = document.createElement('div');
        hint.className = 'epx-dmn-props__hint';
        hint.textContent = connected.map((d) => d.name || d.id).join(', ');
        const wrap = document.createElement('div');
        wrap.appendChild(btn);
        wrap.appendChild(hint);
        g.appendChild(field('Sync bindings', wrap));
      }
    }
    return g;
  }

  _decisionsInvokingBkm(bkm) {
    const defs = bkm && bkm.$parent;
    if (!defs || !defs.drgElement) return [];
    const bkmName = bkm.name;
    return defs.drgElement.filter((drg) => {
      if (drg.$type !== 'dmn:Decision') return false;
      const logic = drg.decisionLogic;
      if (!logic || logic.$type !== 'dmn:Invocation') return false;
      const linkedByKr = (drg.knowledgeRequirement || []).some((kr) => {
        const ref = kr.requiredKnowledge;
        const rid = ref && (ref.id || (ref.href && ref.href.replace(/^#/, '')));
        return rid === bkm.id;
      });
      if (linkedByKr) return true;
      const called = logic.calledFunction;
      return !!(called && (called.text === bkmName || called.text === bkm.id));
    });
  }

  _buildParametersEditor(fnDef) {
    if (!fnDef.formalParameter) fnDef.formalParameter = [];
    const wrap = document.createElement('div');
    wrap.className = 'epx-dmn-props__params';

    const header = document.createElement('div');
    header.className = 'epx-dmn-props__label';
    header.textContent = 'Parameters';
    wrap.appendChild(header);

    const list = document.createElement('div');
    list.className = 'epx-dmn-props__params-list';
    wrap.appendChild(list);

    const renderList = () => {
      list.innerHTML = '';
      fnDef.formalParameter.forEach((p, idx) => {
        const row = document.createElement('div');
        row.className = 'epx-dmn-props__params-row';

        const nameIn = bindNoSpaces(text(p.name || '', (val) => {
          p.name = sanitizeFeelIdent(val) || null;
          this._notify();
        }));
        nameIn.placeholder = 'name';
        row.appendChild(nameIn);

        const typeIn = typeSelect(p.typeRef || 'string', (val) => {
          p.typeRef = val || null;
          this._notify();
        });
        row.appendChild(typeIn);

        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'epx-dmn-props__params-remove';
        rm.title = 'Remove';
        rm.textContent = '×';
        rm.addEventListener('click', () => {
          fnDef.formalParameter.splice(idx, 1);
          renderList();
          this._notify();
        });
        row.appendChild(rm);

        list.appendChild(row);
      });
    };
    renderList();

    const addBtn = button('+ Add parameter', () => {
      const item = this._moddle.create('dmn:InformationItem', {
        id: 'Var_' + Math.random().toString(36).slice(2, 9),
        name: '',
        typeRef: 'string',
      });
      fnDef.formalParameter.push(item);
      renderList();
      this._notify();
    });
    wrap.appendChild(addBtn);
    return wrap;
  }

  _buildRequirementGroup(element) {
    const g = group('Requirement');
    const href =
      (element.requiredDecision && element.requiredDecision.href) ||
      (element.requiredInput && element.requiredInput.href) ||
      (element.requiredKnowledge && element.requiredKnowledge.href) ||
      (element.requiredAuthority && element.requiredAuthority.href) ||
      null;
    g.appendChild(field('Source', readonly(href || '(none)')));
    return g;
  }

  _notify() {
    this._onChange({ element: this._current });
  }
}

// ── helpers ──────────────────────────────────────────────────────────

function group(title) {
  const g = document.createElement('div');
  g.className = 'epx-dmn-props__group';
  const h = document.createElement('div');
  h.className = 'epx-dmn-props__group-header';
  h.textContent = title;
  g.appendChild(h);
  return g;
}

function field(label, input) {
  const row = document.createElement('div');
  row.className = 'epx-dmn-props__field';
  const lab = document.createElement('label');
  lab.className = 'epx-dmn-props__label';
  lab.textContent = label;
  row.appendChild(lab);
  row.appendChild(input);
  return row;
}

function text(value, onCommit) {
  const input = document.createElement('input');
  input.className = 'epx-dmn-props__input';
  input.type = 'text';
  input.value = value;
  input.addEventListener('blur', () => onCommit(input.value.trim()));
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') input.blur();
  });
  return input;
}

function textarea(value, onCommit) {
  const ta = document.createElement('textarea');
  ta.className = 'epx-dmn-props__textarea';
  ta.value = value;
  ta.rows = 3;
  ta.addEventListener('blur', () => onCommit(ta.value));
  return ta;
}

function readonly(value) {
  const span = document.createElement('span');
  span.className = 'epx-dmn-props__readonly';
  span.textContent = value;
  return span;
}

function badge(value) {
  const b = document.createElement('span');
  b.className = 'epx-dmn-props__badge';
  b.textContent = value;
  return b;
}

function button(label, onClick) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'epx-dmn-props__button';
  b.textContent = label;
  b.addEventListener('click', onClick);
  return b;
}

function typeSelect(value, onCommit) {
  const s = document.createElement('select');
  s.className = 'epx-dmn-props__select';
  FEEL_TYPES.forEach((t) => {
    const opt = document.createElement('option');
    opt.value = t;
    opt.textContent = t;
    if (t === value) opt.selected = true;
    s.appendChild(opt);
  });
  s.addEventListener('change', () => onCommit(s.value));
  return s;
}

const HIT_POLICIES = ['UNIQUE', 'ANY', 'PRIORITY', 'FIRST', 'RULE ORDER', 'OUTPUT ORDER', 'COLLECT'];

function hitPolicySelect(value, onCommit) {
  const s = document.createElement('select');
  s.className = 'epx-dmn-props__select';
  HIT_POLICIES.forEach((t) => {
    const opt = document.createElement('option');
    opt.value = t;
    opt.textContent = t;
    if (t === value) opt.selected = true;
    s.appendChild(opt);
  });
  s.addEventListener('change', () => onCommit(s.value));
  return s;
}

function hasVariable(el) {
  return el.$type === 'dmn:Decision' || el.$type === 'dmn:BusinessKnowledgeModel' || el.$type === 'dmn:InputData';
}
function isDecision(el)     { return el.$type === 'dmn:Decision'; }
function isBkm(el)          { return el.$type === 'dmn:BusinessKnowledgeModel'; }
function isRequirement(el)  {
  return el.$type === 'dmn:InformationRequirement' ||
         el.$type === 'dmn:KnowledgeRequirement' ||
         el.$type === 'dmn:AuthorityRequirement';
}
function supportsName(el) {
  return ['dmn:Decision','dmn:BusinessKnowledgeModel','dmn:InputData','dmn:KnowledgeSource','dmn:DecisionService'].includes(el.$type);
}
function supportsDescription(el) { return supportsName(el); }
